/* eslint-disable no-unused-vars */
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { API_CONFIG } from '../config/apiConfig.js';

class SocketService {
  constructor() {
    this.socket = null;
    this.connectionStatus = 'disconnected';
    this.listeners = new Map();
    this.previousSocketId = null;
    this.username = null;
    this.isShow = true;
  }

  connect(walletAddress = null) {
    // Use centralized configuration for socket URL
    let socketUrl = API_CONFIG.SOCKET_URL;
    const isDevelopment = import.meta.env.DEV;
    
    // In development, if using Vite proxy (window.location.origin),
    // the proxy will handle /socket.io automatically
    // In production, connect directly to backend
    if (isDevelopment && socketUrl === window.location.origin) {
      // Vite proxy handles /socket.io path automatically
      // socket.io-client will use the proxy when connecting to same origin
      // console.log('🔌 Socket connecting via Vite proxy:', socketUrl);
    } else {
      console.log('🔌 Socket connecting directly to backend:', socketUrl);
    }

    // Disconnect existing socket if any
    if (this.socket) {
      this.socket.disconnect();
    }

    // Create connection options optimized for performance and low latency
    const options = {
      // Prioritize WebSocket for better performance and lower latency
      transports: ['websocket', 'polling'],
      upgrade: true, // Allow upgrade from polling to websocket
      forceNew: false, // Reuse existing connection if available
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000, // Reduced timeout for faster connection
      // Additional options for performance
      withCredentials: false,
      autoConnect: true,
      // Disable JSONP polling for security
      jsonp: false,
      // Remember upgrade to websocket for better performance
      rememberUpgrade: true,
      // Add path for socket.io
      path: '/socket.io/',
      // Optimize for low latency
      pingTimeout: 60000,
      pingInterval: 25000,
    };

    // Add wallet address if provided
    if (walletAddress) {
      options.query = { walletAddress };
    }

    // Add ngrok header for socket connections
    options.extraHeaders = {
      'ngrok-skip-browser-warning': 'true',
    };

    this.socket = io(socketUrl, options);
    this.setupEventListeners();
    
    
    return this.socket;
  }

  rejoin(roomId, oldId, username) {
    if (this.socket) {
      console.log('🔄 DEBUG: Manual rejoin attempt', {
        roomId,
        oldId,
        username,
        currentSocketId: this.socket.id,
        socketConnected: this.socket.connected
      });
      this.socket.emit('rejoin', { roomId, oldId, username });
    } else {
      console.log('❌ DEBUG: Cannot rejoin - no socket connection');
    }
  }

  setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      // console.log('🔗 Socket connected successfully');
      this.connectionStatus = 'connected';
      this.notifyListeners('connectionStatus', 'connected');

      // Handle reconnection with previous socket ID
      if (this.previousSocketId) {
        const currentRoom = localStorage.getItem('currentRoom');
        const sessionData = this.getSessionData();
        console.log('🔄 DEBUG: Reconnection attempt via REJOIN', {
          previousSocketId: this.previousSocketId,
          currentSocketId: this.socket.id,
          currentRoom,
          sessionData,
          hasSessionData: !!sessionData
        });
        
        if (currentRoom) {
          console.log('🔄 Emitting rejoin event:', currentRoom, this.previousSocketId);
          this.socket.emit('rejoin', { room: currentRoom, oldId: this.previousSocketId });
        }
        this.previousSocketId = null;
      } else {
        // console.log('🔄 DEBUG: No previousSocketId, will try session recovery instead');
        // console.log('🔄 DEBUG: This means the page was refreshed or connection was lost abruptly');
      }

      // Handle URL hash for direct room join
      const urlHash = window.location.hash.substring(1);
      if (urlHash) {
        this.notifyListeners('roomInvite', urlHash);
      }

      // Attempt session recovery
      const sessionData = this.getSessionData();
      if (sessionData && sessionData.username) {
        // let isShow = true;
        // console.log('🔄 DEBUG: Session recovery attempt via RECOVER_SESSION', {
        //   sessionData,
        //   socketId: this.socket.id,
        //   username: sessionData.username,
        //   roomId: sessionData.roomId,
        //   oldSocketId: sessionData.socketId
        // });
        console.log('🔄 Emitting recoverSession event...');
        this.username = sessionData.username; // Set username for UI components
        this.socket.emit('recoverSession', sessionData);
        if(this.isShow){
        setTimeout(() => {
          toast.success('Session recovered successfully');
        }, 1000);
        this.isShow = false;
        }
      } else {
        // console.log('🔄 DEBUG: No session data available for recovery');
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
      this.connectionStatus = 'error';
      this.notifyListeners('connectionStatus', 'error');
      
      // Provide more specific error messages for common issues
      let errorMessage = error.message;
      if (error.message.includes('Network Error') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = 'Cannot connect to game server. Please make sure the backend server is running on port 3000.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Connection timeout. Please check your internet connection and try again.';
      }
      
      this.notifyListeners('connectionError', errorMessage);
    });

    this.socket.on('disconnect', (reason) => {
      // console.log('🔗 DEBUG: Socket disconnected', {
      //   reason,
      //   socketId: this.socket.id,
      //   currentRoom: this.getCurrentRoom(),
      //   sessionData: this.getSessionData()
      // });
      this.previousSocketId = this.socket.id;
      this.connectionStatus = 'disconnected';
      this.notifyListeners('connectionStatus', 'disconnected');
    });

    this.socket.on('reconnect', () => {
      console.log('🔗 Socket reconnected');
      this.connectionStatus = 'connected';
      this.notifyListeners('connectionStatus', 'connected');
    });

    // Game events
    this.socket.on('playerListUpdate', (players) => {
      this.notifyListeners('playerListUpdate', players);
    });

    this.socket.on('readyStatusUpdate', ({ id, ready }) => {
      this.notifyListeners('readyStatusUpdate', { id, ready });
    });

    this.socket.on('countdownUpdate', (seconds) => {
      this.notifyListeners('countdownUpdate', seconds);
    });

    this.socket.on('countdownCancel', () => {
      this.notifyListeners('countdownCancel');
    });

    this.socket.on('gameStart', ({ roomId, rejoin = false }) => {
      const sessionData = { roomId, socketId: this.socket.id, username: this.getUsername() };
      // console.log('🔄 DEBUG: Game start event', {
      //   roomId,
      //   rejoin,
      //   sessionData,
      //   currentSocketId: this.socket.id
      // });
      localStorage.setItem('sessionData', JSON.stringify(sessionData));
      this.notifyListeners('gameStart', { roomId, rejoin });
      if (rejoin) {
        console.log('🔄 Rejoined game successfully');
      }
    });

    this.socket.on('message', (msg) => {
      // Check if this is a grace period message
      if (msg.includes('was disconnected. Reconnecting in 5 minutes')) {
        console.log('⏰ DEBUG: Grace period message received', { message: msg });
        this.notifyListeners('gracePeriodStarted', msg);
      }
      this.notifyListeners('message', msg);
    });

    this.socket.on('update', (newBoard) => {
      try {
        const board = JSON.parse(newBoard);
        this.notifyListeners('boardUpdate', board);
      } catch (error) {
        console.error('❌ Error parsing board data:', error);
      }
    });

    this.socket.on('gameOver', (msg) => {
      localStorage.removeItem('sessionData');
      this.notifyListeners('gameOver', msg);
    });

    this.socket.on('winnerUpdateBroadcast', (data) => {
      this.notifyListeners('winnerUpdate', data);
    });

    this.socket.on('playerDied', (player) => {
      if (player.id === this.socket.id) {
        localStorage.removeItem('sessionData');
      }
      this.notifyListeners('playerDied', player);
    });

    this.socket.on('sessionRecovered', ({ roomId, username }) => {
      console.log('✅ DEBUG: Session recovered successfully', {
        roomId,
        username,
        currentSocketId: this.socket.id,
        sessionData: this.getSessionData()
      });
      this.notifyListeners('sessionRecovered', { roomId, username });
    });

    this.socket.on('sessionRecoveryFailed', (message) => {
      console.log('❌ DEBUG: Session recovery failed', {
        message,
        currentSocketId: this.socket.id,
        sessionData: this.getSessionData()
      });
      localStorage.removeItem('sessionData');
      this.notifyListeners('sessionRecoveryFailed', message);
    });

    this.socket.on('roomAssigned', (roomId) => {
      localStorage.setItem('currentRoom', roomId);
      if (!roomId.startsWith('public_')) {
        window.history.replaceState(null, null, `#${roomId}`);
      }
      this.notifyListeners('roomAssigned', roomId);
    });

    this.socket.on('roomOver', (message) => {
      localStorage.removeItem('sessionData');
      localStorage.removeItem('currentRoom');
      this.notifyListeners('roomOver', message);
    });

    this.socket.on('playerAlert', ({ message, customIcon, type = '' }) => {
      this.notifyListeners('playerAlert', { message, customIcon, type });
    });

    this.socket.on('arsenalSelect', (slot) => {
      this.notifyListeners('arsenalSelect', slot);
    });

    this.socket.on('arsenalUpdate', (arsenalItems) => {
      this.notifyListeners('arsenalUpdate', arsenalItems);
    });
    
    this.socket.on('rejoinSuccess', ({ roomId, username }) => {
      // console.log('✅ DEBUG: Successfully rejoined game', {
      //   roomId,
      //   username,
      //   currentSocketId: this.socket.id,
      //   sessionData: this.getSessionData()
      // });
      this.notifyListeners('rejoinSuccess', { roomId, username });
    });

  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Socket actions
  join(roomId = 'public', username = null, walletAddress = null) {
    if (this.socket) {
      this.socket.emit('join', { 
        roomId, 
        username: username || this.username || `Player${Math.floor(Math.random() * 1000)}`,
        address: walletAddress 
      });
      localStorage.setItem('currentRoom', roomId);
    }
  }

  setUsername(username, walletAddress = null) {
    if (this.socket) {
      // Set username in service for UI components
      this.username = username;
      
      // Send both username and wallet address
      const data = {
        username: username,
        walletAddress: walletAddress || localStorage.getItem('walletAddress') || null
      };
      
      this.socket.emit('setUsername', data);
      localStorage.setItem('username', username);
    }
  }

  // Removed: recoverSession method

  playerReady(ready) {
    if (this.socket) {
      this.socket.emit('playerReady', ready);
    }
  }

  sendMessage(message) {
    if (this.socket) {
      this.socket.emit('message', message);
    }
  }

  move(direction) {
    if (this.socket) {
      this.socket.emit('move', direction);
    }
  }

  plantBomb() {
    if (this.socket) {
      this.socket.emit('plantBomb');
    }
  }

  changeArsenalItem() {
    if (this.socket) {
      this.socket.emit('changeArsenalItem');
    }
  }

  leaveRoom() {
    if (this.socket) {
      this.socket.emit('leaveRoom');
      localStorage.removeItem('sessionData');
      localStorage.removeItem('currentRoom');
    }
  }

  // Utility methods
  getSessionData() {
    const sessionData = localStorage.getItem('sessionData');
    return sessionData ? JSON.parse(sessionData) : null;
  }

  getUsername() {
    return localStorage.getItem('username') || '';
  }

  getCurrentRoom() {
    return localStorage.getItem('currentRoom') || '';
  }

  getId() {
    return this.socket ? this.socket.id : null;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

// Create singleton instance
const socketService = new SocketService();
export default socketService;
